import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, Info, Loader2, Search } from 'lucide-react';
import { Input } from 'components/ui/input';
import { AvailabilityBadge } from 'components/AvailabilityBadge';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';
import { useTechniciansList } from 'hooks/useTechnicians';
import { technicianFullName } from 'pages/AdminTechnicians/csv';
import { apiErrorMessage } from 'utils/errorMessage';
import type { TechnicianListItem } from 'api/techniciansApi';
import { HELP, SKILL_TAG } from './helpBlock';
import ProposeButton from './ProposeButton';

/**
 * « Rechercher » — the manual technician search, by name (D20). Not a fallback: 1 technician out
 * of 133 carries a perimeter and 0 carry a specialty, so the ranking returns nobody on almost
 * every mission and this is *the* usable path — offered whether or not the engine proposed anyone.
 * The suggestions ignore the perimeter and the availability, and each row says both instead.
 * An inactive account is a block and says why in words; being outside the department is a warning
 * and still sendable, the send recording the bypass (D22). ⚠️ Nothing here may suggest the
 * technician will confirm or decline — that mobile screen is L4c, undelivered.
 */

interface TechnicianSearchPanelProps {
  /**
   * The mission's department, or `null` when no postal code resolved one. Null means the perimeter
   * cannot be computed, so no row claims a mismatch — a third state, not a false negative.
   */
  subdivision: { id: number; code: string; name: string } | null;
  disabled: boolean;
  /**
   * No postal code on the mission, so the send has no department and the API answers a generic
   * 400 — on ≈ 11 % of DPGP tickets, the population this search exists for (D19).
   */
  noPostalCode: boolean;
  /**
   * The ranking proposed nobody — loading, error, empty or no department alike. The section then
   * opens itself: it is the only remaining path, and collapsed it would hide the whole screen.
   */
  rankedEmpty: boolean;
  /** The technician this drawer's send is in flight for, whichever list started it. */
  sendingTo: number | null;
  onSend: (technician: TechnicianListItem) => void;
}

/** Below this, nothing is fetched: an open empty field must not pull the whole directory. */
const MIN_SEARCH = 2;
/** Suggestions, not a directory page — the screen for browsing all of them is elsewhere. */
const SUGGESTIONS = 10;

const PANEL_ID = 'technician-search';
/** Rendered with the rows it describes, never behind another condition (R-027). */
const NO_CODE_ID = 'technician-search-no-postal-code';
const TechnicianSearchPanel = ({
  subdivision,
  disabled,
  noPostalCode,
  rankedEmpty,
  sendingTo,
  onSend,
}: TechnicianSearchPanelProps) => {
  const { t } = useTranslation();
  // Null until the operator decides; his click then wins for good, whatever the ranking does next.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? rankedEmpty;
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [input, setInput] = useDebouncedSearch(search, setSearch);

  const term = search ?? '';
  const enough = term.length >= MIN_SEARCH;
  // `useTechniciansList` has no `enabled` of its own — gated here, or an empty field would ask
  // for the pool. No `geoAreaId`, no `availability`, no `status`: every matching name comes back.
  const results = useTechniciansList(
    { search: term, orderBy: 'name', order: 'asc', page: 1, pageSize: SUGGESTIONS },
    open && enough
  );
  const found = results.data?.technicians ?? [];

  /** Both ids are `geo_subdivisions.id` — the directory joins the referential, the mission resolves it. */
  const outsidePerimeter = (technician: TechnicianListItem): boolean =>
    !!subdivision && !technician.geoAreas.some((area) => area.id === subdivision.id);

  return (
    <div className="flex flex-col gap-2 border-t pt-[14px]">
      <button
        type="button"
        className="flex min-h-[44px] items-center gap-1.5 self-start text-[12.5px] font-medium text-foreground underline-offset-2 hover:underline"
        aria-expanded={open}
        // Never point an ARIA relationship at an id behind a conditional render (R-027, R-038).
        aria-controls={open ? PANEL_ID : undefined}
        onClick={() => setToggled(!open)}
      >
        <Search className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        {t('Search')}
        {/* Same disclosure chevron as the « Filtres » collapse, rotation included. */}
        <ChevronDown
          className={`h-[15px] w-[15px] text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div id={PANEL_ID} className="flex flex-col gap-2">
          <Input
            aria-label={t('Search a technician by name…')}
            placeholder={t('Search a technician by name…')}
            value={input}
            autoComplete="off"
            maxLength={80}
            onChange={(event) => setInput(event.target.value)}
          />
          <p className="text-[11.5px] leading-relaxed text-muted-foreground/80">
            {t('Suggestions ignore the department and the availability. Sending outside the department is allowed and recorded.')}
          </p>
          {/* The reason, not just a grey button: this is the very ticket the search exists for. */}
          {noPostalCode && (
            <p id={NO_CODE_ID} className="text-[12px] leading-relaxed text-amber-700 dark:text-amber-400">
              {t('Fill in the store postal code above before sending: without a department, the request is refused.')}
            </p>
          )}

          {!enough ? (
            <p className={HELP}>
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
              {t('Type at least {{min}} characters of a name to search.', { min: MIN_SEARCH })}
            </p>
          ) : results.isLoading ? (
            <div className="flex items-center justify-center py-6" role="status">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">{t('Loading…')}</span>
            </div>
          ) : results.error ? (
            <p className="text-[13px] text-rose-600 dark:text-rose-400" role="alert">
              {apiErrorMessage(t, results.error)}
            </p>
          ) : found.length === 0 ? (
            /* A different sentence from « type a name »: nothing matched is an answer, not a prompt. */
            <p className={HELP}>
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
              {t('No technician matches this name.')}
            </p>
          ) : (
            <ul
              aria-label={t('Search a technician by name…')}
              className="flex max-h-[260px] flex-col gap-[9px] overflow-y-auto pr-0.5"
            >
              {found.map((technician) => (
                <li key={technician.id} className="rounded-xl border p-3">
                  <p className="truncate text-[13.5px] font-medium">
                    {technicianFullName(technician)}
                    {technician.profile.company && (
                      <span className="ml-1.5 font-normal text-muted-foreground">· {technician.profile.company}</span>
                    )}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <AvailabilityBadge state={technician.availability} />
                    {/* In words, both ways: greyed alone never says why a button is dead. */}
                    <span className="text-[12px] text-muted-foreground">
                      {technician.isActive ? t('Active account') : t('Inactive account — cannot be sent to')}
                    </span>
                  </div>
                  {/* What he is sent for, drawn as the ranked card draws it — and said in words
                      when there is none, so an empty row never reads as a broken render. */}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {technician.specialties.length === 0 ? (
                      <span className="text-[12px] text-muted-foreground/70">{t('No skill declared')}</span>
                    ) : (
                      technician.specialties.map((specialty) => (
                        <span key={specialty.id} className={SKILL_TAG}>
                          {specialty.name}
                        </span>
                      ))
                    )}
                  </div>
                  {/* A warning, not a block — and only where the department is known. */}
                  {outsidePerimeter(technician) && (
                    <p className="mt-2 flex items-start gap-1.5 text-[12px] text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {t('This technician does not cover this department')}
                    </p>
                  )}
                  <div className="mt-3 flex justify-end">
                    <ProposeButton
                      label={t('Propose')}
                      sending={sendingTo === technician.id}
                      disabled={disabled || !technician.isActive}
                      describedBy={noPostalCode ? NO_CODE_ID : undefined}
                      onClick={() => onSend(technician)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default TechnicianSearchPanel;
